/**
 * NotificationContext
 * Phase 4 Hybrid Approach - Delegates to React Query internally
 * 
 * Maintains backward compatibility with existing Context API while
 * using React Query hooks for data fetching and state management.
 * 
 * Integrates real-time Supabase subscriptions with React Query cache invalidation.
 */

import { useAuth } from '@/hooks/useAuth';
import type { Notification } from '@/types/notification';
import React, { createContext, ReactNode, useContext } from 'react';
import {
  useNotificationsQuery,
  useUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useClearReadNotificationsMutation,
} from '@/hooks/queries/useNotifications';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { logger } from "@/lib/logger";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // ========================================================================
  // Phase 4 Hybrid Approach: Use React Query hooks internally
  // Context API remains for backward compatibility, delegates to React Query
  // ========================================================================
  
  const { 
    data: notificationsData, 
    isLoading, 
    error: queryError,
    refetch
  } = useNotificationsQuery({ unreadOnly: false });
  
  const { data: unreadCount = 0 } = useUnreadCountQuery();
  
  const markAsReadMutation = useMarkAsReadMutation();
  const markAllAsReadMutation = useMarkAllAsReadMutation();
  const deleteNotificationMutation = useDeleteNotificationMutation();
  const clearReadMutation = useClearReadNotificationsMutation();

  // ========================================================================
  // Real-time Supabase Subscription
  // Automatically invalidates React Query cache on INSERT/UPDATE/DELETE
  // ========================================================================
  const handleNotificationUpdate = React.useCallback(async () => {
    // React Query hook handles cache invalidation automatically
    // This callback is for any additional side effects if needed
      logger.warn('📡 Real-time notification update received, React Query cache invalidated');
  }, []);

  useRealtimeNotifications({
    onNotificationUpdate: handleNotificationUpdate
  });
  
  // Extract notifications array from React Query data
  const notifications = notificationsData?.notifications || [];
  
  // ========================================================================
  // Delegate Context API methods to React Query mutations
  // ========================================================================
  
  const refreshNotifications = async () => {
    await refetch();
  };
  
  const markAsRead = async (notificationIds: string[]) => {
    if (!user?.id) {return;}
    await markAsReadMutation.mutateAsync(notificationIds);
  };

  const markAllAsRead = async () => {
    if (!user?.id) {return;}
    await markAllAsReadMutation.mutateAsync();
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user?.id) {return;}
    await deleteNotificationMutation.mutateAsync(notificationId);
  };

  const clearRead = async () => {
    if (!user?.id) {return;}
    await clearReadMutation.mutateAsync();
  };

  const contextValue: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    error: queryError?.message || null,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearRead,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
